import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, Shield, Radio, ArrowUpRight, ArrowDownLeft, Building2,
  Send, Zap, Activity, ChevronRight, Loader2, Landmark, Sparkles,
  Cpu, KeyRound, Wifi, TrendingUp, Users, Fingerprint,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { useVCBStages } from "../components/vcb/StageTracker";
import { StageGate, CreditLiquidityPanel } from "../components/vcb/StageGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) =>
  `₹${Number(Math.round((n || 0) * 100) / 100).toLocaleString("en-IN",
    { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const mono = (n) =>
  Number(n || 0).toLocaleString("en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function authHeaders() {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/* ────────────────────────────────────────────────────────────────
   TERMINAL-WIDE STYLE (scoped keyframes)
──────────────────────────────────────────────────────────────── */
const TerminalStyles = () => (
  <style>{`
    @keyframes vcb-shine-sweep {
      0%   { transform: translateX(-120%) rotate(18deg); opacity: 0; }
      15%  { opacity: 1; }
      60%  { opacity: 1; }
      100% { transform: translateX(220%) rotate(18deg); opacity: 0; }
    }
    @keyframes vcb-emerald-pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(47,185,153,0.35),
                             0 0 14px rgba(47,185,153,0.35); }
      50%     { box-shadow: 0 0 0 6px rgba(47,185,153,0.00),
                             0 0 22px rgba(47,185,153,0.55); }
    }
    @keyframes vcb-ticker-scroll {
      0%   { transform: translateY(0); }
      100% { transform: translateY(-50%); }
    }
    @keyframes vcb-ripple {
      to { transform: scale(4); opacity: 0; }
    }
    @keyframes vcb-scanline {
      0%   { transform: translateY(-100%); }
      100% { transform: translateY(1200%); }
    }
    .vcb-shine::before {
      content: "";
      position: absolute; top: 0; left: 0;
      width: 50%; height: 100%;
      background: linear-gradient(115deg,
        transparent 0%,
        rgba(246,228,166,0.10) 45%,
        rgba(246,228,166,0.38) 50%,
        rgba(246,228,166,0.10) 55%,
        transparent 100%);
      transform: translateX(-120%) rotate(18deg);
      animation: vcb-shine-sweep 5.5s cubic-bezier(.5,.05,.5,.95) infinite;
      pointer-events: none;
      mix-blend-mode: screen;
    }
    .vcb-brushed {
      background:
        linear-gradient(135deg, rgba(255,255,255,0.02) 0%,
                                  rgba(0,0,0,0.15) 100%),
        repeating-linear-gradient(90deg,
          rgba(255,255,255,0.010) 0px,
          rgba(255,255,255,0.010) 1px,
          transparent 1px,
          transparent 3px),
        linear-gradient(135deg, #14171D 0%, #0B0D12 100%);
    }
    .vcb-panel {
      background: linear-gradient(180deg, #0B0D12 0%, #08090C 100%);
      border: 1px solid #1E222A;
    }
    .vcb-glass {
      background: rgba(255,255,255,0.025);
      backdrop-filter: blur(18px) saturate(140%);
      -webkit-backdrop-filter: blur(18px) saturate(140%);
      border: 1px solid rgba(216,178,62,0.18);
    }
    .vcb-glass:hover {
      border-color: rgba(216,178,62,0.50);
      background: rgba(255,255,255,0.045);
      transform: translateY(-4px);
      box-shadow: 0 20px 55px rgba(216,178,62,0.10),
                  0 0 0 1px rgba(216,178,62,0.25) inset;
    }
    .vcb-ripple-dot {
      position: fixed;
      pointer-events: none;
      border-radius: 9999px;
      border: 1px solid rgba(216,178,62,0.65);
      width: 12px; height: 12px;
      z-index: 60;
      transform-origin: center;
      animation: vcb-ripple 0.7s ease-out forwards;
    }
    .cinzel { font-family: 'Cinzel', 'Playfair Display', serif;
              letter-spacing: 0.14em; text-transform: uppercase; }
    .vcb-mono { font-family: 'JetBrains Mono', 'IBM Plex Mono',
                'Source Code Pro', monospace; }
    .vcb-grid-bg {
      background-image:
        radial-gradient(ellipse 1200px 400px at 50% -10%,
          rgba(216,178,62,0.08) 0%, transparent 60%),
        linear-gradient(rgba(30,34,42,0.35) 1px, transparent 1px),
        linear-gradient(90deg, rgba(30,34,42,0.35) 1px, transparent 1px);
      background-size: auto, 44px 44px, 44px 44px;
      background-color: #050506;
    }
    .vcb-badge-live {
      animation: vcb-emerald-pulse 2.2s ease-in-out infinite;
    }
  `}</style>
);

/* ────────────────────────────────────────────────────────────────
   DATA RIPPLE — global click feedback
──────────────────────────────────────────────────────────────── */
function useDataRipple() {
  useEffect(() => {
    const onDown = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      // Only ripple on real actionable elements
      const clickable = t.closest("button, a, [data-ripple], [role='button']");
      if (!clickable) return;
      const dot = document.createElement("span");
      dot.className = "vcb-ripple-dot";
      dot.style.left = `${e.clientX - 6}px`;
      dot.style.top = `${e.clientY - 6}px`;
      document.body.appendChild(dot);
      setTimeout(() => dot.remove(), 750);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);
}

/* ────────────────────────────────────────────────────────────────
   SVG Circular Gauge
──────────────────────────────────────────────────────────────── */
function Gauge({ value = 0, max = 100, label, sub, color = "#D8B23E",
                 caption, testid }) {
  const pct = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
  const R = 62;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - pct);
  return (
    <div className="vcb-panel rounded-xl p-5 relative overflow-hidden"
         data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="cinzel text-[10px] text-neutral-400">{label}</div>
          {sub && <div className="text-[10px] text-neutral-600 mt-0.5">{sub}</div>}
        </div>
        <div className="vcb-mono text-[10px] text-neutral-500 tracking-widest">
          {Math.round(pct * 100)}%
        </div>
      </div>
      <div className="flex items-center justify-center py-2">
        <svg width="160" height="160" viewBox="0 0 160 160"
             className="drop-shadow-[0_0_12px_rgba(216,178,62,0.12)]">
          <defs>
            <linearGradient id={`grad-${testid}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={color} stopOpacity="1" />
              <stop offset="100%" stopColor={color} stopOpacity="0.5" />
            </linearGradient>
          </defs>
          <circle cx="80" cy="80" r={R} stroke="#1A1D24" strokeWidth="8"
                  fill="none" />
          <motion.circle
            cx="80" cy="80" r={R}
            stroke={`url(#grad-${testid})`} strokeWidth="8" fill="none"
            strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1.6, ease: "easeOut" }}
            transform="rotate(-90 80 80)"
          />
          <text x="80" y="82" textAnchor="middle"
                className="vcb-mono"
                fill="#F5F5F0" fontSize="18" fontWeight="700">
            {caption}
          </text>
          <text x="80" y="104" textAnchor="middle" fill="#6B7280"
                fontSize="9" className="cinzel">
            LIQUIDITY
          </text>
        </svg>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Shine Balance Card
──────────────────────────────────────────────────────────────── */
function ShineCard({ label, amount, sub, icon: Icon, tone = "gold",
                     testid }) {
  const border = tone === "gold" ? "border-[#D8B23E]/30"
                                 : "border-[#2FB999]/30";
  const accent = tone === "gold" ? "#D8B23E" : "#2FB999";
  return (
    <div
      className={`relative overflow-hidden rounded-xl p-6 vcb-brushed
                  ${border} border vcb-shine`}
      data-testid={testid}
    >
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="cinzel text-[10px] text-neutral-400">{label}</div>
          {Icon && <Icon className="w-4 h-4" style={{ color: accent }} />}
        </div>
        <div className="vcb-mono text-3xl font-bold text-[#F6E4A6] tracking-tight">
          {amount}
        </div>
        {sub && (
          <div className="mt-2 vcb-mono text-[10px] text-neutral-500 tracking-widest">
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Control Center Tile (Glassmorphism 3D float)
──────────────────────────────────────────────────────────────── */
function ControlTile({ label, sub, icon: Icon, onClick, disabled, testid,
                       accent = "#D8B23E" }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={`vcb-glass rounded-xl p-5 text-left transition-all duration-300
                  disabled:opacity-40 disabled:cursor-not-allowed
                  disabled:hover:transform-none disabled:hover:border-[#D8B23E]/18
                  group`}
      style={{ "--accent": accent }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center
                        border border-white/10 bg-black/40"
             style={{ boxShadow: `0 0 14px ${accent}22` }}>
          {Icon && <Icon className="w-4 h-4" style={{ color: accent }} />}
        </div>
        <ChevronRight className="w-4 h-4 text-neutral-600
                                 group-hover:text-[#D8B23E]
                                 group-hover:translate-x-1 transition-all" />
      </div>
      <div className="cinzel text-xs text-neutral-100">{label}</div>
      <div className="vcb-mono text-[10px] text-neutral-500 mt-1 tracking-widest">
        {sub}
      </div>
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────
   Live UTC Clock
──────────────────────────────────────────────────────────────── */
function AtomicClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const t = now.toISOString().slice(11, 19);
  const d = now.toISOString().slice(0, 10);
  return (
    <div className="hidden md:flex items-center gap-3 vcb-mono text-[11px]"
         data-testid="vcb-atomic-clock">
      <span className="text-neutral-500 tracking-widest">{d}</span>
      <span className="text-[#F6E4A6] tracking-widest">{t}Z</span>
      <span className="text-neutral-600 tracking-widest">UTC</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Trade Stream Ticker (vertical, auto-scroll, curated fallback)
──────────────────────────────────────────────────────────────── */
const CURATED_STREAM = [
  { rail: "IMPS",   amount: 480000, from: "VNC-SRN-4A21", to: "VNC-SRN-8F0D" },
  { rail: "RTGS",   amount: 2450000, from: "VNC-SRN-00C1", to: "VNC-SRN-9E11" },
  { rail: "NEFT",   amount: 128500, from: "VNC-SRN-7B54", to: "VNC-SRN-BB03" },
  { rail: "INTRA",  amount: 62000, from: "VNC-SRN-3F19", to: "VNC-SRN-7A28" },
  { rail: "UPI",    amount: 14900, from: "VNC-SRN-6C05", to: "VNC-SRN-2D77" },
  { rail: "IMPS",   amount: 385000, from: "VNC-SRN-9A2B", to: "VNC-SRN-C412" },
  { rail: "RTGS",   amount: 1180000, from: "VNC-SRN-5F80", to: "VNC-SRN-0E44" },
  { rail: "INTRA",  amount: 92300, from: "VNC-SRN-EB1A", to: "VNC-SRN-52C8" },
];
const RAIL_TONE = {
  IMPS: "#D8B23E", RTGS: "#F6E4A6", NEFT: "#B87333",
  INTRA: "#2FB999", UPI: "#67E8F9",
};

function TradeStream({ liveRows = [] }) {
  // Merge live rows and curated to always feel alive
  const rows = useMemo(() => {
    const live = liveRows.slice(0, 6).map((l) => ({
      rail: (l.rail || "INTRA").toUpperCase(),
      amount: l.amount,
      from: l.from || "VNC-SRN-••••",
      to:   l.to   || "VNC-SRN-••••",
      live: true,
    }));
    const seed = CURATED_STREAM;
    // If we have <2 live entries, blend with curated for the elite feel
    const mix = live.length >= 2 ? [...live, ...seed] : [...live, ...seed];
    return [...mix, ...mix]; // duplicate for seamless scroll
  }, [liveRows]);

  return (
    <div className="vcb-panel rounded-xl overflow-hidden h-[420px] relative"
         data-testid="vcb-trade-stream">
      <div className="flex items-center justify-between px-5 py-3
                      border-b border-[#1E222A]">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#34D399]
                           vcb-badge-live" />
          <span className="cinzel text-[11px] text-neutral-200">
            Trade Stream · Live
          </span>
        </div>
        <span className="vcb-mono text-[10px] text-neutral-500 tracking-widest">
          MAPPED NODES
        </span>
      </div>
      <div className="relative h-[365px] overflow-hidden
                      [mask-image:linear-gradient(180deg,transparent,black_12%,black_88%,transparent)]">
        <div className="absolute inset-x-0 top-0"
             style={{ animation: "vcb-ticker-scroll 42s linear infinite" }}>
          {rows.map((r, i) => (
            <div key={i}
                 className="px-5 py-3 border-b border-[#141821]
                            hover:bg-white/[0.02] transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="vcb-mono text-[10px] tracking-widest"
                      style={{ color: RAIL_TONE[r.rail] || "#D8B23E" }}>
                  {r.rail} {r.live && "· LIVE"}
                </span>
                <span className="vcb-mono text-xs text-[#F6E4A6]">
                  ₹{mono(r.amount)}
                </span>
              </div>
              <div className="flex items-center justify-between vcb-mono text-[10px]
                              text-neutral-500 tracking-widest">
                <span>{r.from}</span>
                <ArrowUpRight className="w-3 h-3 text-neutral-600" />
                <span>{r.to}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Rails Distribution mini-bar
──────────────────────────────────────────────────────────────── */
function RailsDistribution({ statement = [] }) {
  const dist = useMemo(() => {
    const buckets = { WALLET: 0, UPI: 0, IMPS: 0, NEFT: 0, RTGS: 0 };
    statement.forEach((s) => {
      const m = (s?.meta?.payment_mode || "wallet").toUpperCase();
      if (buckets[m] != null) buckets[m] += Math.abs(s.amount || 0);
    });
    const total = Object.values(buckets).reduce((a, b) => a + b, 0);
    // Fallback demo distribution
    if (total === 0) {
      return [
        { rail: "WALLET", pct: 42, color: "#2FB999" },
        { rail: "IMPS",   pct: 24, color: "#D8B23E" },
        { rail: "RTGS",   pct: 18, color: "#F6E4A6" },
        { rail: "NEFT",   pct: 10, color: "#B87333" },
        { rail: "UPI",    pct: 6,  color: "#67E8F9" },
      ];
    }
    const colorMap = { WALLET: "#2FB999", IMPS: "#D8B23E", RTGS: "#F6E4A6",
                       NEFT: "#B87333", UPI: "#67E8F9" };
    return Object.entries(buckets).map(([rail, v]) => ({
      rail, pct: Math.round((v / total) * 100), color: colorMap[rail],
    })).sort((a, b) => b.pct - a.pct);
  }, [statement]);

  return (
    <div className="vcb-panel rounded-xl p-5" data-testid="vcb-rails-distribution">
      <div className="flex items-center justify-between mb-4">
        <div className="cinzel text-xs text-neutral-200">Rails Distribution</div>
        <span className="vcb-mono text-[10px] text-neutral-500 tracking-widest">
          30D
        </span>
      </div>
      <div className="space-y-3">
        {dist.map((d) => (
          <div key={d.rail}>
            <div className="flex items-center justify-between mb-1">
              <span className="vcb-mono text-[10px] tracking-widest text-neutral-400">
                {d.rail}
              </span>
              <span className="vcb-mono text-[11px] text-[#F6E4A6]">{d.pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#12141A] overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${d.color}, ${d.color}66)` }}
                initial={{ width: 0 }}
                animate={{ width: `${d.pct}%` }}
                transition={{ duration: 1.2, ease: "easeOut" }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Recent Transactions Ledger (dense, mono)
──────────────────────────────────────────────────────────────── */
function LedgerTable({ rows = [] }) {
  const display = rows.slice(0, 12);
  return (
    <div className="vcb-panel rounded-xl overflow-hidden"
         data-testid="vcb-ledger-table">
      <div className="flex items-center justify-between px-5 py-3
                      border-b border-[#1E222A]">
        <div className="flex items-center gap-2">
          <Cpu className="w-3.5 h-3.5 text-[#D8B23E]" />
          <span className="cinzel text-[11px] text-neutral-200">
            Institutional Ledger · Last {display.length} Entries
          </span>
        </div>
        <span className="vcb-mono text-[10px] text-neutral-500 tracking-widest">
          SIGNED · HASHED
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full vcb-mono text-[11px]">
          <thead>
            <tr className="text-neutral-500 tracking-widest bg-[#0A0B0E]">
              <th className="text-left px-5 py-2 font-normal">TS · UTC</th>
              <th className="text-left px-3 py-2 font-normal">REF</th>
              <th className="text-left px-3 py-2 font-normal">RAIL</th>
              <th className="text-left px-3 py-2 font-normal">MEMO</th>
              <th className="text-right px-5 py-2 font-normal">AMOUNT (INR)</th>
            </tr>
          </thead>
          <tbody>
            {display.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-neutral-600">
                  No entries yet. Your first sovereign transaction will appear here.
                </td>
              </tr>
            )}
            {display.map((r, i) => {
              const rail = (r?.meta?.payment_mode || "wallet").toUpperCase();
              const isCr = r.type === "credit" || (r.amount || 0) > 0;
              return (
                <tr key={r.tx_id || i}
                    className="border-t border-[#141821] hover:bg-white/[0.02]">
                  <td className="px-5 py-2.5 text-neutral-400">
                    {(r.created_at || "").slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2.5 text-neutral-300">
                    {(r.reference || r.tx_id || "").slice(0, 22)}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="px-1.5 py-0.5 rounded-sm border tracking-widest"
                          style={{
                            color: RAIL_TONE[rail] || "#D8B23E",
                            borderColor: (RAIL_TONE[rail] || "#D8B23E") + "44",
                          }}>
                      {rail}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-neutral-500 truncate max-w-[220px]">
                    {r.description || r?.meta?.memo || "—"}
                  </td>
                  <td className={`px-5 py-2.5 text-right tabular-nums
                                  ${isCr ? "text-[#34D399]" : "text-[#F6E4A6]"}`}>
                    {isCr ? "+" : "−"} {mono(Math.abs(r.amount || 0))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Lockdown Screen (non-inducted)
──────────────────────────────────────────────────────────────── */
function LockdownScreen() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen vcb-grid-bg text-neutral-200 relative
                    overflow-hidden flex items-center justify-center px-6"
         data-testid="vcb-lockdown-screen">
      <div className="pointer-events-none absolute inset-0
                      bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(255,69,58,0.10)_60%,rgba(0,0,0,0.6)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px]
                      bg-[#FF453A]/40"
           style={{ animation: "vcb-scanline 5s linear infinite" }} />
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-2xl w-full text-center relative z-10 vcb-panel
                   rounded-2xl p-10 border-[#FF453A]/40"
        style={{ boxShadow: "0 0 60px rgba(255,69,58,0.18)" }}
      >
        <div className="mx-auto w-14 h-14 rounded-full grid place-items-center
                        border border-[#FF453A]/50 mb-6"
             style={{ boxShadow: "0 0 24px rgba(255,69,58,0.35)" }}>
          <Lock className="w-6 h-6 text-[#FF453A]" />
        </div>
        <div className="cinzel text-[10px] text-[#FF453A] tracking-widest mb-3">
          Restricted Access · Level 5 Clearance Missing
        </div>
        <h1 className="cinzel text-3xl md:text-4xl font-extrabold
                        text-[#FF453A] tracking-widest mb-4">
          Sovereign Lockdown
        </h1>
        <p className="text-neutral-400 text-sm leading-relaxed max-w-md mx-auto">
          The Vinci Core Banking Terminal is reserved for accounts that have
          completed the official Sovereign Induction. Your entity must be
          formally inducted before institutional rails, mapped ledgers and
          zero-fee sovereign flows are activated.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="/vcb" data-testid="vcb-lockdown-request-access"
             className="inline-flex items-center justify-center gap-2 bg-[#D8B23E] text-black px-5 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.28em] hover:bg-[#E5C450]">♛ Premium · Request Access</a>
          <Button
            onClick={() => nav("/wallet")}
            data-testid="vcb-lockdown-wallet-btn"
            variant="outline"
            className="border-[#1E222A] bg-transparent text-neutral-300
                        hover:bg-white/[0.04] hover:text-white cinzel text-xs"
          >
            Return to Wallet
          </Button>
          <Button
            onClick={() => nav("/wallet")}
            data-testid="vcb-lockdown-induction-btn"
            className="bg-gradient-to-r from-[#FF453A] to-[#B87333]
                        hover:from-[#FF6B60] hover:to-[#D8B23E] text-black
                        font-bold cinzel text-xs px-8"
          >
            Initiate VCB Induction
          </Button>
        </div>
        <div className="mt-6 vcb-mono text-[10px] text-neutral-600 tracking-widest">
          CLEARANCE::VCB-INDUCTION-REQUIRED
        </div>
      </motion.div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   MAIN PAGE
──────────────────────────────────────────────────────────────── */
export default function VCBTerminal() {
  const { user } = useAuth();
  const nav = useNavigate();
  useDataRipple();
  const { data: stageData } = useVCBStages();
  const stageN = stageData?.current_stage || 0;

  const [loading, setLoading] = useState(true);
  const [health, setHealth]   = useState(null);
  const [mappings, setMappings] = useState({ outgoing: [], incoming: [] });
  const [statement, setStatement] = useState([]);
  const [xferOpen, setXferOpen] = useState(false);
  const [xferMode, setXferMode] = useState("wallet");
  const [xferTo, setXferTo]     = useState("");
  const [xferAmount, setXferAmount] = useState("");
  const [xferMemo, setXferMemo] = useState("");
  const [xferBusy, setXferBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const headers = authHeaders();
      const [h, m, s] = await Promise.all([
        axios.get(`${API}/vcb/wallet/health`, { headers })
             .then((r) => r.data).catch(() => null),
        axios.get(`${API}/vcb/mappings/mine`, { headers })
             .then((r) => r.data).catch(() => ({ outgoing: [], incoming: [] })),
        axios.get(`${API}/wallet/statement?limit=50`, { headers })
             .then((r) => r.data?.rows || []).catch(() => []),
      ]);
      setHealth(h);
      setMappings(m);
      setStatement(s);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vcbStatus = health?.vcb_status || user?.vcb_status || "none";
  const isInducted = vcbStatus === "inducted";

  /* ── Derived numbers ── */
  const balance   = health?.balance || 0;
  const floor     = health?.operating_floor || 5000;
  const canTx     = !!health?.can_transact && stageN >= 3;
  const activeMappings = (mappings.outgoing || []).filter(m => m.status === "accepted")
    .concat((mappings.incoming || []).filter(m => m.status === "accepted"));
  const uniquePartners = new Set(activeMappings.map(m =>
    m.parent_email === user?.email ? m.child_email : m.parent_email)).size;

  const zeroFeeVolume = useMemo(() => statement
    .filter(r => r?.meta?.vcb_intra_transfer)
    .reduce((sum, r) => sum + Math.abs(r.amount || 0), 0), [statement]);

  const tradePower = Math.max(0, balance - floor);
  const tradePowerMax = Math.max(balance * 1.5, 100000);

  /* ── Live trade stream — pluck last 6 VCB transfers ── */
  const liveStream = useMemo(() => statement
    .filter(r => r?.meta?.vcb_intra_transfer)
    .slice(0, 6)
    .map(r => ({
      rail: (r?.meta?.payment_mode || "wallet").toUpperCase(),
      amount: Math.abs(r.amount || 0),
      from: (r?.type === "debit") ? r.user_email : r?.meta?.counterparty_email,
      to:   (r?.type === "debit") ? r?.meta?.counterparty_email : r.user_email,
    })), [statement]);

  /* ── VCB Transfer submit ── */
  const submitTransfer = async () => {
    const amount = parseFloat(xferAmount);
    if (!xferTo || !amount) {
      toast.error("Counterparty and amount required."); return;
    }
    try {
      setXferBusy(true);
      const { data } = await axios.post(`${API}/vcb/transfer`, {
        counterparty_email: xferTo,
        amount,
        memo: xferMemo,
        payment_mode: xferMode,
      }, { headers: authHeaders() });
      toast.success(`Sovereign transfer complete · ${data.reference}`, {
        description: `${inr(amount)} settled at 0% via ${xferMode.toUpperCase()}`,
      });
      setXferOpen(false);
      setXferTo(""); setXferAmount(""); setXferMemo("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setXferBusy(false);
    }
  };

  /* ── Access gating ── */
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050506] grid place-items-center">
        <Loader2 className="w-6 h-6 text-[#D8B23E] animate-spin" />
      </div>
    );
  }
  if (user === false) { nav("/login"); return null; }
  if (!loading && !isInducted) return <LockdownScreen />;

  /* ── SRN badge from the user's id (deterministic) ── */
  const uid = user?.id || user?._id || "0000";
  const srn = `VNC-SRN-${String(uid).slice(-4).toUpperCase()}·${(user?.email || "").slice(0,2).toUpperCase()}`;

  return (
    <div className="min-h-screen vcb-grid-bg text-neutral-200 relative
                    overflow-x-hidden" data-testid="vcb-terminal-page">
      <TerminalStyles />

      {/* ── Sovereign Encryption Header ── */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#050506]/85
                    border-b border-[#1E222A]"
        data-testid="vcb-terminal-header"
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-3
                        flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-9 h-9 rounded-md grid place-items-center
                            border border-[#D8B23E]/30 bg-black/40"
                 style={{ boxShadow: "0 0 18px rgba(216,178,62,0.15)" }}>
              <Landmark className="w-4 h-4 text-[#D8B23E]" />
            </div>
            <div>
              <div className="cinzel text-[11px] tracking-widest text-neutral-100">
                Vinci Core Banking · Sovereign Terminal
              </div>
              <div className="vcb-mono text-[10px] text-neutral-500 tracking-widest">
                INSTITUTIONAL CLEARING HOUSE · v3.1
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <AtomicClock />
            <span
              className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full
                          bg-[#2FB999]/10 border border-[#2FB999]/30 vcb-mono
                          text-[10px] text-[#34D399] tracking-widest vcb-badge-live"
              data-testid="vcb-encryption-badge"
            >
              <Shield className="w-3 h-3" />
              SOVEREIGN ENCRYPTION ACTIVE
            </span>
            <span
              className="hidden lg:inline-flex vcb-mono text-[10px] text-neutral-400
                          tracking-widest px-2 py-1 border border-[#1E222A]
                          rounded-sm"
              data-testid="vcb-srn-badge"
            >
              <Fingerprint className="w-3 h-3 mr-1 text-[#D8B23E]" />
              {srn}
            </span>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8
                        space-y-6">

        {/* Section title */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="vcb-mono text-[10px] text-[#D8B23E] tracking-widest mb-1">
              LEVEL-5 CLEARANCE · ACCESS GRANTED
            </div>
            <h1 className="cinzel text-3xl md:text-4xl font-extrabold
                            text-[#F6E4A6]">
              Sovereign Liquidity Hub
            </h1>
            <div className="text-neutral-500 text-xs mt-1 vcb-mono tracking-widest">
              {user?.email} · {uniquePartners} MAPPED PARTNERS ·
              {" "}{canTx ? "RAILS ARMED" : "RAILS DORMANT"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => nav("/wallet")}
              data-testid="vcb-terminal-back-wallet"
              className="border-[#1E222A] bg-transparent text-neutral-300
                          hover:bg-white/[0.04] hover:text-white cinzel text-[10px]"
            >
              Legacy Wallet
            </Button>
            <Button
              onClick={() => nav("/vcb/mappings")}
              data-testid="vcb-terminal-mappings-btn"
              className="bg-[#D8B23E] hover:bg-[#F6E4A6] text-black
                          cinzel text-[10px] font-bold"
            >
              Manage Mappings
            </Button>
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {[0,1,2,3].map(i =>
              <div key={i} className="vcb-panel rounded-xl h-[220px]
                                      animate-pulse" />)}
          </div>
        )}

        {!loading && (
          <>
            {/* ── LIQUIDITY HUB · 4 Gauges ── */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Gauge
                testid="vcb-gauge-trade-power"
                label="Trade Power"
                sub="Above ₹5K floor"
                color="#D8B23E"
                value={tradePower}
                max={tradePowerMax}
                caption={`₹${(tradePower / 1000).toFixed(1)}K`}
              />
              <Gauge
                testid="vcb-gauge-wallet-float"
                label="Wallet Float"
                sub="Live sovereign balance"
                color="#2FB999"
                value={balance}
                max={Math.max(balance * 1.4, 50000)}
                caption={`₹${(balance / 1000).toFixed(1)}K`}
              />
              <Gauge
                testid="vcb-gauge-operating-guard"
                label="Operating Guard"
                sub={`₹${floor.toLocaleString("en-IN")} floor`}
                color="#B87333"
                value={Math.min(balance, floor)}
                max={floor}
                caption={balance >= floor ? "OK" : "LOW"}
              />
              <Gauge
                testid="vcb-gauge-mapped-nodes"
                label="Mapped Nodes"
                sub="Sovereign network"
                color="#F6E4A6"
                value={uniquePartners}
                max={Math.max(uniquePartners * 2, 5)}
                caption={String(uniquePartners)}
              />
            </section>

            {/* ── Shine Balance Cards ── */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ShineCard
                testid="vcb-shine-balance"
                label="Wallet Balance"
                amount={inr(balance)}
                sub="SETTLED · READY · SIGNED"
                icon={Sparkles}
              />
              <ShineCard
                testid="vcb-shine-mapped-turnover"
                label="30-Day Mapped Turnover"
                amount={inr(zeroFeeVolume)}
                sub="ZERO-FEE INTRA VOLUME"
                icon={TrendingUp}
              />
              <ShineCard
                testid="vcb-shine-tx-limit"
                label="Sovereign TX Limit"
                amount="UNLIMITED"
                sub="INTRA-ECOSYSTEM · 0%"
                icon={Zap}
                tone="emerald"
              />
            </section>

            {/* ── Middle Split · Control Center + Trade Stream ── */}
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Control Center · Stage III */}
              <div className="lg:col-span-7 space-y-4">
               <StageGate stages={stageData} need={3} testid="vcb-gate-transaction-desk" label="Control Center">
                <div className="flex items-center justify-between">
                  <div className="cinzel text-xs text-neutral-300">
                    Institutional Control Center
                  </div>
                  <span className="vcb-mono text-[10px] text-neutral-500
                                    tracking-widest">
                    {canTx ? "◉ ARMED" : "◎ DORMANT"}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  <ControlTile
                    label="Internal Sync"
                    sub="0% · Wallet-to-Wallet"
                    icon={Radio}
                    onClick={() => { setXferMode("wallet"); setXferOpen(true); }}
                    disabled={!canTx}
                    accent="#2FB999"
                    testid="vcb-control-internal-sync"
                  />
                  <ControlTile
                    label="IMPS · Immediate"
                    sub="Instant · 24×7"
                    icon={Zap}
                    onClick={() => { setXferMode("imps"); setXferOpen(true); }}
                    disabled={!canTx}
                    accent="#D8B23E"
                    testid="vcb-control-imps"
                  />
                  <ControlTile
                    label="NEFT · Batched"
                    sub="Net Bulk Rails"
                    icon={Send}
                    onClick={() => { setXferMode("neft"); setXferOpen(true); }}
                    disabled={!canTx}
                    accent="#B87333"
                    testid="vcb-control-neft"
                  />
                  <ControlTile
                    label="RTGS · High-Value"
                    sub="Real-Time Gross"
                    icon={Building2}
                    onClick={() => { setXferMode("rtgs"); setXferOpen(true); }}
                    disabled={!canTx}
                    accent="#F6E4A6"
                    testid="vcb-control-rtgs"
                  />
                  <ControlTile
                    label="UPI · Retail"
                    sub="Merchant Rails"
                    icon={Wifi}
                    onClick={() => { setXferMode("upi"); setXferOpen(true); }}
                    disabled={!canTx}
                    accent="#67E8F9"
                    testid="vcb-control-upi"
                  />
                  <ControlTile
                    label="Sovereign Vault"
                    sub="Signed Records"
                    icon={KeyRound}
                    onClick={() => nav("/account/documents")}
                    accent="#D8B23E"
                    testid="vcb-control-vault"
                  />
                </div>
               </StageGate>

                {/* Mapped Customers strip */}
                <div className="vcb-panel rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-3.5 h-3.5 text-[#D8B23E]" />
                      <span className="cinzel text-[11px] text-neutral-200">
                        Mapped Sovereign Network
                      </span>
                    </div>
                    <span className="vcb-mono text-[10px] text-neutral-500
                                      tracking-widest">
                      {uniquePartners} NODES
                    </span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1"
                       data-testid="vcb-mapped-roster">
                    {activeMappings.length === 0 && (
                      <div className="text-xs text-neutral-500 py-2">
                        No mapped partners yet. Invite your first counterparty.
                      </div>
                    )}
                    {activeMappings.map((m, i) => {
                      const peer = m.parent_email === user?.email
                                     ? m.child_email : m.parent_email;
                      return (
                        <div key={m.id || i}
                             className="flex items-center gap-2 px-3 py-1.5 rounded-full
                                        border border-[#1E222A] bg-[#0A0B0E]
                                        whitespace-nowrap"
                             data-testid="vcb-mapped-chip">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#34D399]
                                            vcb-badge-live" />
                          <span className="vcb-mono text-[10px] text-neutral-300
                                            tracking-widest">
                            {peer}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Trade Stream · Stage III */}
              <div className="lg:col-span-5">
                <StageGate stages={stageData} need={3} testid="vcb-gate-trade-stream" className="h-full" label="Live trade stream" compact>
                  <TradeStream liveRows={liveStream} />
                </StageGate>
              </div>
            </section>

            {/* ── Credit & Liquidity · Stage IV ── */}
            <section>
              <StageGate stages={stageData} need={4} testid="vcb-gate-credit" label="Credit tools">
                <CreditLiquidityPanel />
              </StageGate>
            </section>

            {/* ── Ledger + Rails Split ── */}
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8">
                <LedgerTable rows={statement} />
              </div>
              <div className="lg:col-span-4">
                <StageGate stages={stageData} need={5} testid="vcb-gate-reports" className="h-full" label="Reports &amp; analytics" compact>
                  <RailsDistribution statement={statement} />
                </StageGate>
              </div>
            </section>

            <footer className="pt-4 border-t border-[#1E222A] flex flex-wrap
                                 justify-between gap-3 vcb-mono text-[10px]
                                 text-neutral-600 tracking-widest">
              <span>© VINCI INC · SOVEREIGN FINANCIAL INFRASTRUCTURE</span>
              <span className="flex items-center gap-2">
                <Activity className="w-3 h-3 text-[#34D399] vcb-badge-live" />
                LIVE · SIGNED · IMMUTABLE · AES-256
              </span>
            </footer>
          </>
        )}
      </main>

      {/* ── Transfer Dialog ── */}
      <Dialog open={xferOpen} onOpenChange={setXferOpen}>
        <DialogContent className="bg-[#0A0B0E] border-[#1E222A] text-neutral-200"
                       data-testid="vcb-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="cinzel text-lg text-[#F6E4A6]">
              Sovereign Transfer · {xferMode.toUpperCase()}
            </DialogTitle>
            <DialogDescription className="text-neutral-500 text-xs">
              Wallet-to-wallet settlement inside the Vinci sovereign network.
              Zero handling charges. Instant reconciliation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-neutral-400 cinzel">
                Counterparty Email
              </Label>
              <Input
                value={xferTo} onChange={(e) => setXferTo(e.target.value)}
                placeholder="partner@company.com"
                data-testid="vcb-xfer-to-input"
                className="bg-black/40 border-[#1E222A] vcb-mono text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-neutral-400 cinzel">
                Amount (INR)
              </Label>
              <Input
                type="number" value={xferAmount}
                onChange={(e) => setXferAmount(e.target.value)}
                placeholder="10000"
                data-testid="vcb-xfer-amount-input"
                className="bg-black/40 border-[#1E222A] vcb-mono text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-neutral-400 cinzel">
                Memo (optional)
              </Label>
              <Input
                value={xferMemo}
                onChange={(e) => setXferMemo(e.target.value)}
                placeholder="Invoice / reference"
                data-testid="vcb-xfer-memo-input"
                className="bg-black/40 border-[#1E222A] vcb-mono text-sm mt-1"
              />
            </div>
            <div className="rounded-md bg-[#2FB999]/5 border border-[#2FB999]/20 p-3
                            vcb-mono text-[10px] text-[#34D399] tracking-widest">
              FEE: 0% · SETTLEMENT: INSTANT · SIGNED: YES
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setXferOpen(false)}
              data-testid="vcb-xfer-cancel-btn"
              className="border-[#1E222A] bg-transparent text-neutral-300
                          cinzel text-xs"
            >
              Cancel
            </Button>
            <Button
              onClick={submitTransfer}
              disabled={xferBusy}
              data-testid="vcb-xfer-submit-btn"
              className="bg-[#D8B23E] hover:bg-[#F6E4A6] text-black
                          cinzel text-xs font-bold"
            >
              {xferBusy && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
              Dispatch Sovereign Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
